import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-body2 q-mb-xs file-label"
}
const _hoisted_2 = { class: "row no-wrap items-center q-pa-sm q-gutter-xs" }
const _hoisted_3 = { class: "col" }
const _hoisted_4 = {
  key: 0,
  class: "q-uploader__title text-weight-regular"
}
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = {
  key: 2,
  class: "row q-uploader__title text-weight-regular"
}
const _hoisted_7 = {
  key: 3,
  class: "q-uploader__title text-weight-regular"
}
const _hoisted_8 = {
  key: 1,
  class: "text-center q-mt-lg"
}

import { QUploader, useFormChild } from 'quasar';
import { computed, inject, ref, useAttrs, watch } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import ExistingFilePicker from 'SharedComponents/Attachments/ExistingFilePicker.vue';
import SIcon from 'SharedComponents/ui/Icon/SIcon.vue';
import EntityLink from 'SharedComponents/ui/Link/EntityLink.vue';
import useFileApi from 'SharedDir/common/composables/useFileApi';
import { makeRequest } from 'SharedDir/common/composables/useRequests';
import { EmitValue, PropValue, useValue } from 'SharedDir/common/composables/useValue';
import {
  ALLOW_PICK_EXISTING_FILES_KEY,
  FILE_DETAILS_ROUTE_KEY,
  FileDetailsRouteResolver,
} from 'SharedDir/constants/file-uploader';
import { FilePropValidationRule } from 'SharedDir/constants/files';
import {
  AttachmentType,
  AttachmentsApiType,
  FileFull,
  RejectedFile,
} from 'SharedDir/declarations/files';
import {
  MakeFileFailedDescription,
  mapFileTypeToExtensions,
} from 'SharedDir/service/file/uploader';

interface FileUploaderOldProps {
  label?: string;
  acceptExtensions?: string | Array<string>;
  acceptSize?: number;
  readonly?: boolean;
  isDisplayImage?: boolean;
  fileUploading?: boolean;
  errorMessage?: string;
  required?: boolean;
  removable?: boolean;
  api: AttachmentsApiType;
  token?: string;
  formFields?: Record<string, unknown> | null | undefined;
  useRefreshToken?: boolean;
  autoUpload?: boolean;
  wide?: boolean;
  /**
   * Показывать ли кнопку выбора из ранее загруженных файлов
   * (`ExistingFilePicker`). По умолчанию — `true`; точечно отключается
   * `:allow-pick-existing="false"` (например, в форме редактирования
   * самого файла, чтобы не подменять файл им же).
   */
  allowPickExisting?: boolean;
  /**
   * Маршрут карточки файла для `EntityLink`. Если не задан — используется
   * `FILE_DETAILS_ROUTE_KEY` из `provide` (admin). По умолчанию ссылка скрыта.
   */
  fileDetailsRoute?: FileDetailsRouteResolver | null;
}
type InnerValueType = Pick<AttachmentType, 'id' | 'name' | 'extension'> | null;

export default /*@__PURE__*/_defineComponent({
  ...{ name: 'QuasarFileUploader' },
  __name: 'QuasarFileUploader',
  props: {
    modelValue: {},
    label: { default: '' },
    acceptExtensions: {},
    acceptSize: { default: 5120 },
    readonly: { type: Boolean, default: false },
    isDisplayImage: { type: Boolean, default: false },
    fileUploading: { type: Boolean, default: false },
    errorMessage: {},
    required: { type: Boolean, default: false },
    removable: { type: Boolean, default: true },
    api: {},
    token: {},
    formFields: {},
    useRefreshToken: { type: Boolean, default: true },
    autoUpload: { type: Boolean, default: true },
    wide: { type: Boolean, default: false },
    allowPickExisting: { type: Boolean, default: undefined },
    fileDetailsRoute: { type: [Function, null], default: undefined }
  },
  emits: ["update:modelValue", "remove", "rejected"],
  setup(__props: any, { expose: __expose, emit: __emit }) {



const attrs = useAttrs();

const props = __props;

const injectedAllowPickExisting = inject(ALLOW_PICK_EXISTING_FILES_KEY, true);
const effectiveAllowPickExisting = computed(
  () => props.allowPickExisting ?? injectedAllowPickExisting
);

const injectedFileDetailsRoute = inject(FILE_DETAILS_ROUTE_KEY, null);
const emit = __emit;
const { innerValue, emitValue } = useValue(props, emit);

const resolvedFileDetailsLocation = computed((): RouteLocationRaw | null => {
  if (!innerValue.value?.id) {
    return null;
  }
  const resolver = props.fileDetailsRoute ?? injectedFileDetailsRoute;
  if (!resolver) {
    return null;
  }
  return resolver(innerValue.value.id);
});
const innerError = ref(false);
const innerErrorMessage = ref(props.errorMessage);

watch(
  () => props.errorMessage,
  (newMessage) => {
    innerErrorMessage.value = newMessage;
  }
);

const hasError = computed(() => innerErrorMessage.value);

const fileUploader = ref<QUploader>(null as unknown as QUploader);

const maxSize = computed(() => (props.acceptSize ? props.acceptSize * 1024 : undefined));

const resetValidation = () => {
  innerError.value = false;
  innerErrorMessage.value = '';
  fileUploader.value.reset();
};

const validtyAcceptExtensions = computed((): string => {
  // Мэппинг если допустимый тип файлов передан как массив
  let extensions: Array<string> = [];
  const acceptExtensions = Array.isArray(props.acceptExtensions)
    ? props.acceptExtensions
    : [props.acceptExtensions];
  acceptExtensions.forEach((acceptExtensionType) => {
    const acceptFileExtensions = mapFileTypeToExtensions[acceptExtensionType];
    if (acceptFileExtensions) {
      extensions = extensions.concat(
        acceptFileExtensions.map((extension) => (extension === '*' ? '*' : `.${extension}`))
      );
    }
  });

  return extensions.join(',');
});

const formFieldsAsEntries = computed(() =>
  Object.entries(props.formFields || {}).map(([name, value]) => ({ name, value }))
);

const getImage = computed(() => props.api.GET(innerValue.value?.id as string).url);

const validate = () => {
  console.debug('Validating ...');
  const valid = !!innerValue.value?.id;
  if (props.required && !valid) {
    innerError.value = !valid;
    innerErrorMessage.value = 'Поле обязательно для заполнения!';
    return valid;
  }
  return true;
};

function focus() {
  // do nothing, this method is required for handling form validation
}

const fileDownloading = ref(false);

const { downloadFile, removeFile } = useFileApi();

const download = async () => {
  if (!innerValue.value) return;
  await makeRequest(() => downloadFile(innerValue.value?.id), {
    startLoading: () => {
      fileDownloading.value = true;
    },
    finishLoading: () => {
      fileDownloading.value = false;
    },
  });
};

const uploadFileFunction = (): unknown => ({
  ...props.api.UPLOAD,
  headers: [
    {
      name: 'Authorization',
      value: `Bearer ${props.token}`,
    },
  ],
});

const successUploadedFile = (info: { files: Array<unknown>; xhr: XMLHttpRequest }): void => {
  const r = JSON.parse(info.xhr.response);
  if (r) {
    innerValue.value = (({ id, name }) => ({ id, name }))(r);
    emitValue();
    resetValidation();
  }
};

const failedUploadedFile = async () => {};

const getFailedRuleDescription = (rejected: RejectedFile): string => {
  return MakeFileFailedDescription(rejected, {
    [FilePropValidationRule.maxFileSize]: props.acceptSize,
    [FilePropValidationRule.accept]: validtyAcceptExtensions.value,
  });
};

const handleRejection = (rejectedList: Array<RejectedFile>) => {
  innerError.value = true;
  innerErrorMessage.value = rejectedList.map(getFailedRuleDescription).join('<br/>');
  fileUploader.value.reset();
  emit('rejected', rejectedList);
};

const remove = async () => {
  innerValue.value = null;
  emitValue();
  emit('remove');
};

const existingPickerShow = ref(false);

function openExistingPicker() {
  existingPickerShow.value = true;
}

function applyPickedExisting(files: FileFull[]) {
  const file = files[0];
  if (!file) return;
  innerValue.value = { id: file.id, name: file.name, extension: file.extension };
  emitValue();
  resetValidation();
}

__expose({ validate, focus, remove });

useFormChild({ validate, resetValidation });

return (_ctx: any,_cache: any) => {
  const _component_q_spinner = _resolveComponent("q-spinner")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_tooltip = _resolveComponent("q-tooltip")!
  const _component_q_uploader_add_trigger = _resolveComponent("q-uploader-add-trigger")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_img = _resolveComponent("q-img")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["q-field q-validation-component", { 'text-negative': hasError.value, 's-input--required': _ctx.required }])
  }, [
    (_ctx.label)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _renderSlot(_ctx.$slots, "label", {
            scopeLabel: _ctx.label,
            download: _unref(downloadFile)
          }, () => [
            _createTextVNode(_toDisplayString(_ctx.label), 1)
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(QUploader), {
      ref_key: "fileUploader",
      ref: fileUploader,
      class: _normalizeClass(["s-form_uploader", _ctx.wide ? '' : 'slim-form']),
      label: _ctx.label,
      flat: "",
      color: hasError.value ? 'white' : 'brand',
      bordered: hasError.value,
      "text-color": hasError.value ? 'negative' : 'white',
      batch: "",
      "hide-upload-btn": "",
      "auto-upload": _ctx.autoUpload,
      "field-name": "file",
      "no-thumbnails": "",
      readonly: _ctx.readonly,
      "form-fields": formFieldsAsEntries.value,
      "max-file-size": maxSize.value,
      accept: validtyAcceptExtensions.value,
      factory: uploadFileFunction,
      onUploaded: successUploadedFile,
      onFailed: failedUploadedFile,
      onRejected: handleRejection
    }, {
      header: _withCtx((scope) => [
        _createElementVNode("div", _hoisted_2, [
          (scope.isUploading || _ctx.fileUploading || fileDownloading.value)
            ? (_openBlock(), _createBlock(_component_q_spinner, {
                key: 0,
                class: "q-uploader__spinner"
              }))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_3, [
            (scope.isUploading)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(scope.uploadSizeLabel) + " / " + _toDisplayString(scope.uploadProgressLabel), 1))
              : (hasError.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: "q-uploader__title text-weight-regular",
                    innerHTML: innerErrorMessage.value
                  }, null, 8, _hoisted_5))
                : (_unref(innerValue) && _unref(innerValue).id)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      _createElementVNode("div", {
                        class: "like-a",
                        onClick: download
                      }, [
                        _createVNode(_component_q_icon, {
                          class: "s-icon medium",
                          name: "las la-file-download"
                        }),
                        _createTextVNode(" " + _toDisplayString(_unref(innerValue).name || _unref(innerValue).id), 1)
                      ]),
                      (resolvedFileDetailsLocation.value)
                        ? (_openBlock(), _createBlock(EntityLink, {
                            key: 0,
                            location: resolvedFileDetailsLocation.value,
                            label: "",
                            inverted: "",
                            "show-open-in-new-icon": ""
                          }, null, 8, ["location"]))
                        : _createCommentVNode("", true)
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _renderSlot(_ctx.$slots, "uploader-title", { title: "Загрузите сюда ваш файл" }, () => [
                        _cache[1] || (_cache[1] = _createTextVNode(" Загрузите сюда ваш файл "))
                      ])
                    ]))
          ]),
          _renderSlot(_ctx.$slots, "header-actions"),
          (_unref(innerValue) && _unref(innerValue).id && _ctx.removable && !_ctx.readonly)
            ? (_openBlock(), _createBlock(SIcon, {
                key: 1,
                name: "app:delete2",
                size: "middle",
                onClick: remove
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_q_tooltip, {
                    "content-class": "s-tooltip",
                    delay: 400
                  }, {
                    default: _withCtx(() => _cache[2] || (_cache[2] = [
                      _createTextVNode("Удалить вложение")
                    ])),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (effectiveAllowPickExisting.value && !_ctx.readonly && scope.canAddFiles)
            ? (_openBlock(), _createBlock(SIcon, {
                key: 2,
                name: "search",
                size: "middle",
                onClick: openExistingPicker
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_q_tooltip, {
                    "content-class": "s-tooltip",
                    delay: 400
                  }, {
                    default: _withCtx(() => _cache[3] || (_cache[3] = [
                      _createTextVNode(" Выбрать из ранее загруженных ")
                    ])),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (scope.canAddFiles)
            ? (_openBlock(), _createBlock(_component_q_btn, {
                key: 3,
                type: "a",
                icon: "folder_open",
                dense: "",
                flat: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_q_uploader_add_trigger),
                  _createVNode(_component_q_tooltip, {
                    "content-class": "s-tooltip",
                    delay: 400
                  }, {
                    default: _withCtx(() => _cache[4] || (_cache[4] = [
                      _createTextVNode(" Выбрать из файлов на диске ")
                    ])),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      list: _withCtx(() => _cache[5] || (_cache[5] = [])),
      _: 3
    }, 8, ["class", "label", "color", "bordered", "text-color", "auto-upload", "readonly", "form-fields", "max-file-size", "accept"]),
    (_ctx.isDisplayImage && _ctx.modelValue?.id)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _createVNode(_component_q_img, {
            src: getImage.value,
            width: "200px",
            height: "200px"
          }, null, 8, ["src"])
        ]))
      : _createCommentVNode("", true),
    (effectiveAllowPickExisting.value)
      ? (_openBlock(), _createBlock(ExistingFilePicker, {
          key: 2,
          modelValue: existingPickerShow.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((existingPickerShow).value = $event)),
          extensions: validtyAcceptExtensions.value,
          onPick: applyPickedExisting
        }, null, 8, ["modelValue", "extensions"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})